<template>
  <div>
    <search-bar-sp
      :preflist="propertyPrefList"
      :options="options"
      searchText="検索"
      :update="update"
      :isSearchBody="isSearchBody"
      @input="isSearchBody = $event"
    />


    <div class="c-container">

      <div class="p-map">
        <div class="c-map-label">
          <h3 v-if="selected">{{selected.establishment_body}}{{selected.name}}の学区エリア</h3>
        </div>
        <div v-if="loadingDistrict" class="map-loading-mask">
          <Loading />
        </div>
        <div v-if="districtErr.isErr" class="map-loading-mask error-mask">
          <p class="p-errmsg">{{districtErr.errMsg}}</p>
        </div>
        <school-area-map
          v-if="isMapReady && selected"
          :key="selected.id"
          :district="selected.district"
          :schoolPoint="schoolCoords"
          :zoom="zoom"
          :labelName="selected.name"
        />
      </div>
      <section class="p-list">

        <div v-if="err.isErr" class="modal-body">
          <p class="p-errmsg">{{err.errMsg}}</p>
        </div>

        <school-districts-list-body
          v-if="selected"
          v-model="selected"
          class="p-list-body-wrapper"
          :cityName="storedLocation.city.name"
          :cityCode="storedLocation.city.code"
          :prefCode="storedLocation.pref.code"
          :items="sortedSchoolList"
          :containsHits="containsHits"
        />
        <search-area-info class="p-list--area--outline" v-if="isAreaInfoDisplay && areaInfo" :info="areaInfo" />
        <school-districts-list-search :preflist="preflist" class="p-list-search-wrapper"/>

        <common-breadcrumb class="p-list--breadcrumb--outline" :breadcrumbType="'schoolList'" />
        <common-footer />
      </section>
    </div>
  </div>
</template>

<script>
import MixinAPI from '@/plugins/mixin-api';
import MixinMeta from '@/plugins/mixin-meta';
import isMobile from '@/plugins/isMobile';
import Loading from '@/components/common/Loading';

import SearchBarPc from '@/components/search/kodate/pc/SearchBar';
import SearchBarSp from '@/components/search/kodate/sp/SearchBar';
import SchoolDistrictsListBody from '@/components/search/school-districts/SchoolDistrictsListBody';
import SchoolDistrictsListSearch from '@/components/search/school-districts/SchoolDistrictsListSearch';
import SearchAreaInfo from '@/components/search/kodate/SearchAreaInfo';
import CommonBreadcrumb from '@/components/parts/CommonBreadcrumb';
import CommonFooter from '@/components/parts/CommonFooter';
import CommonGoToTop from '@/components/parts/CommonGoToTop';

// lazy loading map so leaflet script doesn't check for google script early
const SchoolAreaMap = () => import('@/components/parts/SchoolAreaMap');

export default {
  name: 'SchoolDistrictsList',
  mixins: [MixinAPI, MixinMeta, isMobile],
  props: ['preflist', 'propertyPrefList', 'schoolList', 'schoolFlag', 'selectedSchoolId', 'containsHits'],
  components: {
    SchoolAreaMap,
    SearchBarPc,
    SearchBarSp,
    SearchAreaInfo,
    SchoolDistrictsListBody,
    SchoolDistrictsListSearch,
    CommonBreadcrumb,
    CommonFooter,
    CommonGoToTop,
    Loading,
  },
  data() {
    return {
      selected: null,
      loadingDistrict: false,
      district: null,
      sortedSchoolList: [],
      options: {
        prefcode: '0',
        citycode: '0',
        ask: false,
        byBus: false,
        layout: [],
        priceLow: 0,
        priceHigh: 999999999,
        landAreaLow: 0,
        landAreaHigh: 999,
        buildingAreaLow: 0,
        buildingAreaHigh: 999,
        walkin: 20,
        fw: '',
        size: 30,
        order: 0,
        page: 1,
      },
      err: {
        isErr: false,
        errMsg: '',
      },
      districtErr: {
        isErr: false,
        errMsg: '',
      },
      pager: [],
      isMapReady: false,
      isAreaInfoDisplay: true,
      isSearchBody: false,
      areaInfo: null,
      zoom: 14,
    };
  },
  watch: {
    selected() {
      // Using the data returned with the school itself for now -- when API changes this will need to be updated
      // this.fetchSchoolDistrictData(newValue);
    },
  },
  computed: {
    storedLocation() {
      /* eslint dot-notaion: 0 */
      return this.$store.getters['getLocation'];
    },
    storedSearchHistory() {
      return this.$store.getters['getSearchHistory'];
    },
    schoolCoords() {
      return {
        lng: this.selected.position.geometry.coordinates[0],
        lat: this.selected.position.geometry.coordinates[1],
      };
    },
    isLoggedIn() {
      return this.$store.state.user;
    },
  },
  async mounted() {
    window.scrollTo(0, 0);
    this.areaInfo = await this.getAreaInfo();
    const select = this.schoolList.find(s => s.id === this.selectedSchoolId);

    if (select) {
      // move to front of array
      const newArray = this.schoolList.filter(a => a.id !== select.id);
      newArray.unshift(select);
      this.sortedSchoolList = newArray;
    } else {
      this.sortedSchoolList = this.schoolList;
    }
    this.selected = this.sortedSchoolList[0];
    this.mapIsReady();
  },
  methods: {
    async mapIsReady() {
      this.isMapReady = true;
    },
    update(page = 1) {
      let url = location.origin;
      url += `/kodate/area/${this.options.prefcode}/`;
      if (this.options.citycode !== 0 && this.options.citycode !== '0') url += `${this.options.citycode}/`;

      // page
      url += `?page=${page}`;
      // size
      url += `&size=${this.options.size}`;
      // order
      url += `&order=${this.options.order}`;

      // layout
      if (this.options.layout.length > 0) url += `&layout=${this.options.layout.join()}`;
      // 価格未設定を含む
      if (this.options.ask) url += '&ask=true';
      // バス時間を含む
      if (this.options.byBus) url += '&byBus=true';
      // fw
      if (this.options.fw) url += `&fw=${this.options.fw}`;
      // price
      url += `&priceLow=${this.options.priceLow}`;
      url += `&priceHigh=${this.options.priceHigh}`;
      // landarea
      url += `&landAreaLow=${this.options.landAreaLow}`;
      url += `&landAreaHigh=${this.options.landAreaHigh}`;
      // buildingarea
      url += `&buildingAreaLow=${this.options.buildingAreaLow}`;
      url += `&buildingAreaHigh=${this.options.buildingAreaHigh}`;

      // walkin
      url += `&walkin=${this.options.walkin}`;

      location.href = url;
    },
    async getAreaInfo() {
      const info = await this.getAreaRPList(this.storedLocation.pref.code, this.storedLocation.city.code);
      return info.data.contents[0];
    },
    async fetchSchoolDistrictData(school) {
      try {
        this.districtErr.isErr = false;
        this.districtErr.errMsg = '';
        const coords = school.position.geometry.coordinates;
        this.loadingDistrict = true;
        let districts;
        if (this.schoolFlag === '小学校') {
          districts = await this.getElementarySchoolDistricts(coords[1], coords[0]);
        } else {
          districts = await this.getJuniorHighSchoolDistricts(coords[1], coords[0]);
        }
        if (!districts[0]) {
          throw new Error();
        }
        this.district = districts[0];
      } catch (e) {
        this.districtErr.isErr = true;
        this.districtErr.errMsg = '学区のデータが見つかりませんでした。';
      } finally {
        this.loadingDistrict = false;
      }
    },
  },
};

</script>

<style scoped lang="scss">
@import "@/assets/style/parts_responsive.scss";
$tablet-width: 640px;
$desktop-width: 999px;
.c-container {
  flex-direction: column;
  height: 100%;

  .p-map {
    height: 240px;
    position: relative;

    .map-loading-mask {
      height: 100%;
      width: 100%;
      position: absolute;
      z-index: 10000;
      display: flex;
      align-items: center;
      background-color: rgba(255,255,255, 0.8);

      &.error-mask {
        background-color: rgba(255,255,255, 0.8);
      }
    }
  }

  .p-list--area--outline,
  .p-list-body-wrapper {
    padding: 30px 20px;
  }
  .p-list-body-wrapper {
    padding-top: 0px;
  }
  .p-list-search-wrapper {
    margin-top: 30px;
    background-color: #FAFAFA;
  }
}

.p-list {
  padding: 20px 0px;
}

.c-map-label {
  height: 52px;;
  background-color: #FAFAFA;
  display: flex;
  align-items: center;
  justify-content: center;

  h3 {
    font-weight: bold;
    font-size: 16px;
  }
}

@media screen and ( min-width: $tablet-width ) {
.c-container {
  height:calc(100vh - 174px);

  .p-list-body-wrapper {
    width: 620px;
    margin: auto;
  }

  .p-map {
    height: calc(100vh - 154px);
  }

}
}

@media screen and ( min-width: $desktop-width ) {
.c-container {
  display: flex;
  flex-direction: row;

  .p-map {
    flex: 60;
  }
  .p-list {
    flex: 40;
    height: calc(100vh - 154px);
    overflow: auto;
  }
}
}

</style>
